import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, TrendingUp } from "lucide-react";
import { useApi } from "../../dashboard/api";
import { Skeleton } from "./ui";

export const NotificationCenter = () => {
    const { data, loading } = useApi("notifications");
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const close = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    const unread = data?.filter((n) => n.unread).length || 0;

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="notifications-button"
                aria-label="Notifications"
                aria-expanded={open}
                className="relative flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-nx-elev text-nx-sub transition-colors duration-200 hover:text-nx-text"
            >
                <Bell size={15} strokeWidth={1.5} aria-hidden="true" />
                {unread > 0 && (
                    <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-nx-lime" data-testid="notifications-unread-dot" aria-hidden="true" />
                )}
            </button>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.98 }}
                        transition={{ duration: 0.18 }}
                        className="absolute right-0 top-11 w-80 overflow-hidden rounded-xl border border-white/10 bg-nx-elev shadow-[0_16px_48px_rgba(0,0,0,0.6)]"
                        data-testid="notifications-panel"
                    >
                        <p className="border-b border-white/5 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-nx-mute">
                            Notifications
                        </p>
                        {loading || !data ? (
                            <div className="space-y-2 p-4">
                                <Skeleton className="h-9 w-full" />
                                <Skeleton className="h-9 w-full" />
                            </div>
                        ) : (
                            <ul className="max-h-80 overflow-y-auto">
                                {data.map((n) => (
                                    <li
                                        key={n.id}
                                        data-testid={`notification-${n.id}`}
                                        className="flex items-start gap-3 border-b border-white/[0.04] px-4 py-3 transition-colors duration-200 last:border-0 hover:bg-white/[0.02]"
                                    >
                                        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-white/5 bg-nx-soft text-nx-lime">
                                            <TrendingUp size={12} strokeWidth={1.5} aria-hidden="true" />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-xs font-semibold leading-snug text-nx-text">{n.title}</p>
                                            <p className="mt-0.5 text-[11px] text-nx-mute">{n.time}</p>
                                        </div>
                                        {n.unread && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-nx-lime" aria-label="Unread" />}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
